import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import {
  getSeverityColor,
  getSeverityBackgroundColor,
  getSeverityTextColor,
} from '../../../theme/colorMapper';

/**
 * InsightCard Component
 * Reusable card component for displaying individual insights
 */
const InsightCard = ({ insight, type }) => {
  const theme = useTheme();
  const getSeverityIcon = severity => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon />;
      case 'high':
        return <WarningIcon />;
      case 'medium':
      case 'low':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getTrendIcon = direction => {
    return direction === 'increasing' ? (
      <TrendingUpIcon color="error" />
    ) : direction === 'decreasing' ? (
      <TrendingDownIcon color="success" />
    ) : null;
  };

  const getTitle = () => {
    switch (type) {
      case 'system_trend':
        return (insight.trend_type || 'Unknown')
          .replace('_', ' ')
          .toUpperCase();
      case 'template_effectiveness':
        return insight.template_name || insight.template_id;
      case 'error_trend':
        return insight.error_code;
      case 'anomaly':
        return (insight.anomaly_type || 'Unknown')
          .replace('_', ' ')
          .toUpperCase();
      default:
        return 'Unknown';
    }
  };

  const getFooterContent = () => {
    switch (type) {
      case 'system_trend':
        return (
          <>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            >
              Change:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color:
                  insight.change_percentage > 0
                    ? theme.palette.error.main
                    : insight.change_percentage < 0
                      ? theme.palette.success.main
                      : theme.palette.text.primary,
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: 1.2,
              }}
            >
              {insight.change_percentage > 0 ? '+' : ''}
              {insight.change_percentage.toFixed(1)}%
            </Typography>
          </>
        );
      case 'error_trend':
        return (
          <>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            >
              Occurrences:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.error.main,
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: 1.2,
              }}
            >
              {insight.occurrence_count}
            </Typography>
          </>
        );
      case 'anomaly':
        return (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          >
            Detected: {new Date(insight.detected_at).toLocaleString()}
          </Typography>
        );
      default:
        return null;
    }
  };

  const icon =
    type === 'system_trend' ? (
      getTrendIcon(insight.trend_direction)
    ) : type === 'anomaly' ? (
      <WarningIcon
        sx={{
          color: getSeverityColor(theme, insight.severity),
          fontSize: '20px',
          flexShrink: 0,
        }}
      />
    ) : (
      getSeverityIcon(insight.severity)
    );

  return (
    <Card
      sx={{
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '240px',
        overflow: 'hidden',
        '&:hover': {
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%',
          boxSizing: 'border-box',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            width: '100%',
            minWidth: 0,
          }}
        >
          {icon}
          <Typography
            variant="subtitle1"
            sx={{
              flex: 1,
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '0.875rem',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getTitle()}
          </Typography>
          <Chip
            label={insight.severity}
            size="small"
            sx={{
              backgroundColor: getSeverityBackgroundColor(
                theme,
                insight.severity
              ),
              color: getSeverityTextColor(theme, insight.severity),
              border: `1px solid ${theme.palette.divider}`,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
              minWidth: '70px',
              width: '70px',
              justifyContent: 'center',
              flexShrink: 0,
              '& .MuiChip-label': {
                padding: '0 8px',
                textTransform: 'capitalize',
              },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            mb: 1.5,
            lineHeight: 1.6,
            flex: 1,
            width: '100%',
            fontSize: '0.875rem',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            overflow: 'hidden',
          }}
        >
          {insight.summary}
        </Typography>
        {getFooterContent() && (
          <Box
            sx={{
              mt: 'auto',
              width: '100%',
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.5,
            }}
          >
            {getFooterContent()}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

InsightCard.propTypes = {
  insight: PropTypes.object.isRequired,
  type: PropTypes.oneOf([
    'system_trend',
    'template_effectiveness',
    'error_trend',
    'anomaly',
  ]).isRequired,
};

export default InsightCard;
